import React, { useEffect, useState } from "react";
import { Plus, Trash2, Search, Save, Copy, CheckCircle2, ChevronDown, ChevronRight, Layers, Package, Tag, Box, Scissors, StickyNote, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtIDR } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader, Panel } from "../../lib/ui";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { DataTable, ActiveFilters } from "../../components/DataTable";
import { useServerList } from "../../hooks/useServerList";

const num = (n) => (Number(n) || 0).toLocaleString("id-ID", { maximumFractionDigits: 2 });
const subtotal = (c) => (parseFloat(c.usage_per_pcs) || 0) * (1 + (parseFloat(c.waste_pct) || 0) / 100) * (parseFloat(c.unit_price) || 0);

// Kategori tab Resep Produksi → dipetakan ke "kind" komponen BOM di backend
const CATS = [
  { key: "kain", label: "Kain", icon: Layers, group: "fabric", kinds: [["kain_utama", "Kain Utama"], ["kain_kombinasi", "Kain Kombinasi"], ["furing", "Furing"]], unit: "yard" },
  { key: "aksesori", label: "Aksesori", icon: Package, group: "accessory", kinds: [["aksesori", "Aksesori"]], unit: "pcs" },
  { key: "label", label: "Label & Identitas", icon: Tag, group: "accessory", kinds: [["label", "Label"], ["hangtag", "Hangtag"]], unit: "pcs" },
  { key: "packaging", label: "Packaging", icon: Box, group: "accessory", kinds: [["kemasan", "Plastik/Kemasan"]], unit: "pcs" },
  { key: "ongkos", label: "Ongkos Produksi", icon: Scissors, group: "service", kinds: [["ongkos_jahit", "Ongkos Jahit"], ["ongkos_cutting", "Ongkos Cutting"], ["biaya_lain", "Biaya Lain"]], unit: "pcs" },
  { key: "catatan", label: "Catatan Produksi", icon: StickyNote, group: "note", kinds: [] },
];
const catOf = (kind) => CATS.find((c) => c.kinds.some(([v]) => v === kind)) || CATS[0];
const normalizeKind = (c) => {
  const cat = c.material_kind === "fabric" ? CATS[0] : catOf(c.kind);
  const kinds = cat.kinds.map(([v]) => v);
  return kinds.includes(c.kind) ? c.kind : (kinds[0] || c.kind);
};

export default function BOM() {
  const { can } = useAuth();
  const [products, setProducts] = useState([]);
  const [pq, setPq] = useState("");
  const [product, setProduct] = useState(null);
  const [boms, setBoms] = useState([]);
  const [fabrics, setFabrics] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("kain");
  const [showVersions, setShowVersions] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [productColors, setProductColors] = useState([]);
  const [saving, setSaving] = useState(false);
  const write = can("rnd", "production_admin");
  const [hasBom, setHasBom] = useState("all");
  const idxFilters = React.useMemo(() => (hasBom !== "all" ? { has_bom: hasBom } : {}), [hasBom]);
  const idx = useServerList("/production/boms/product-index", { filters: idxFilters, sort: "name", dir: "asc" });
  const openProduct = (id) => api.get(`/products/${id}`).then((r) => selectProduct(r.data)).catch(() => toast.error("Gagal memuat produk"));

  useEffect(() => {
    api.get("/production/fabric-options").then((r) => setFabrics(r.data)).catch(() => {});
    api.get("/production/accessories", { params: { active: "active" } }).then((r) => setAccessories(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    const t = setTimeout(() => { if (pq.length >= 2 && !product) api.get("/products", { params: { q: pq } }).then((r) => setProducts(r.data.slice(0, 20))); else setProducts([]); }, 350);
    return () => clearTimeout(t);
  }, [pq, product]);

  useEffect(() => {
    const pid = new URLSearchParams(window.location.search).get("product");
    if (!pid) return;
    api.get(`/products/${pid}`).then((r) => selectProduct(r.data)).catch(() => {
      api.get("/products").then((r) => { const p = (r.data || []).find((x) => x.id === pid); if (p) selectProduct(p); }).catch(() => {});
    });
    // eslint-disable-next-line
  }, []);

  const activeBom = boms.find((b) => b.active) || null;

  const draftFrom = (bom) => bom ? { id: bom.id, version: bom.version, note: bom.note || "", components: (bom.components || []).map((c) => ({ ...c, kind: normalizeKind(c) })) } : { id: null, version: null, note: "", components: [] };
  const selectProduct = (p) => {
    setProduct(p); setProducts([]); setPq(p.name); setTab("kain"); setShowVersions(false);
    setProductColors([...new Set((p.variants || []).map((v) => v.color).filter(Boolean))]);
    api.get("/production/boms", { params: { product_id: p.id } }).then((r) => { setBoms(r.data); setEditing(draftFrom(r.data.find((b) => b.active) || null)); });
  };
  const reload = (pid) => api.get("/production/boms", { params: { product_id: pid } }).then((r) => setBoms(r.data));

  const addComp = (cat) => {
    const [kind] = cat.kinds[0];
    setEditing((e) => ({ ...e, components: [...e.components, { kind, material_kind: cat.group, material_id: "", name: cat.group === "service" ? cat.kinds[0][1] : "", unit: cat.unit, usage_per_pcs: 1, waste_pct: 0, unit_price: 0, variant_color: "" }] }));
  };
  const setComp = (idxAbs, patch) => setEditing((e) => ({ ...e, components: e.components.map((c, i) => i === idxAbs ? { ...c, ...patch } : c) }));
  const rmComp = (idxAbs) => setEditing((e) => ({ ...e, components: e.components.filter((_, i) => i !== idxAbs) }));

  const onMaterial = (idxAbs, group, id) => {
    if (group === "fabric") { const f = fabrics.find((x) => x.id === id); setComp(idxAbs, { material_id: id, name: f?.name || "", unit: "yard", unit_price: f?.price_per_unit || 0 }); }
    else { const a = accessories.find((x) => x.id === id); setComp(idxAbs, { material_id: id, name: a?.name || "", unit: a?.usage_unit || "pcs", unit_price: a?.price_per_pcs || 0 }); }
  };

  const applyCuttingAvg = async () => {
    if (!product) return;
    try {
      const r = await api.get("/production/boms/cutting-averages", { params: { product_id: product.id } });
      const items = (r.data.items || []).filter((it) => it.avg_yard_per_pcs > 0);
      if (!items.length) return toast.message("Belum ada data cutting selesai (dengan pemakaian kain tercatat) untuk produk ini.");
      let filled = 0;
      setEditing((e) => ({ ...e, components: e.components.map((c) => {
        if (c.material_kind !== "fabric") return c;
        const hit = items.find((it) => it.material_id === c.material_id && (it.color || "") === (c.variant_color || ""));
        if (hit) { filled++; return { ...c, usage_per_pcs: hit.avg_yard_per_pcs }; }
        return c;
      }) }));
      toast.success(filled ? `${filled} kain diisi dari rata-rata cutting. Periksa lalu klik Simpan.` : "Tidak ada kain yang cocok dengan data cutting (cek warna & pilihan kain).");
    } catch (e) { toast.error("Gagal ambil rata-rata cutting"); }
  };

  const applyRecipeToAllColors = () => {
    const normColor = (s) => (s || "").toLowerCase().split("#")[0].replace(/[^a-z]/g, "");
    const fabricIdOf = (colorId) => fabrics.find((f) => f.id === colorId)?.fabric_id;
    const findFabricColor = (fabId, garmentColor) => {
      const gn = normColor(garmentColor);
      if (!gn) return null;
      return fabrics.find((f) => {
        if (f.fabric_id !== fabId) return false;
        const cn = normColor(f.color_name);
        return cn && (cn === gn || (cn.length >= 3 && gn.length >= 3 && (cn.includes(gn) || gn.includes(cn))));
      }) || null;
    };
    const fabComps = (editing.components || []).filter((c) => c.material_kind === "fabric" && (c.variant_color || "").trim());
    if (!fabComps.length) return toast.message("Isi dulu resep kain untuk minimal 1 warna sebagai contoh, lalu klik ini.");
    const tmplColor = fabComps[0].variant_color;
    const tmplRows = fabComps.filter((c) => c.variant_color === tmplColor);
    const definedColors = new Set(fabComps.map((c) => c.variant_color));
    const missing = productColors.filter((c) => !definedColors.has(c));
    if (!missing.length) return toast.message("Semua warna produk sudah punya resep kain.");
    const added = []; const colorsAdded = new Set(); const unmatched = new Set();
    missing.forEach((mc) => {
      tmplRows.forEach((t) => {
        const fabId = fabricIdOf(t.material_id);
        const match = fabId ? findFabricColor(fabId, mc) : null;
        if (match) {
          added.push({ kind: t.kind, material_kind: "fabric", material_id: match.id, name: match.name, unit: "yard",
            usage_per_pcs: t.usage_per_pcs, waste_pct: t.waste_pct, unit_price: match.price_per_unit ?? t.unit_price, variant_color: mc });
          colorsAdded.add(mc);
        } else { unmatched.add(mc); }
      });
    });
    if (!added.length) return toast.error(`Tidak menemukan warna kain yang cocok untuk: ${[...unmatched].join(", ")}. Tambahkan manual.`);
    setEditing((e) => ({ ...e, components: [...e.components, ...added] }));
    const um = [...unmatched].filter((c) => !colorsAdded.has(c));
    toast.success(`${colorsAdded.size} warna dilengkapi dari resep "${tmplColor}"${um.length ? `. Belum ketemu kain untuk: ${um.join(", ")}` : ""}. Periksa & Simpan.`);
  };

  const variantColors = [...new Set((editing?.components || []).map((c) => (c.variant_color || "").trim()).filter(Boolean))];
  const nVar = variantColors.length || 1;
  const perPcs = (c) => ((c.variant_color || "").trim() ? subtotal(c) / nVar : subtotal(c));
  const catOfComp = (c) => (c.material_kind === "fabric" ? CATS[0] : catOf(c.kind));
  const hpp = (editing?.components || []).reduce((s, c) => s + perPcs(c), 0);
  const catCount = (cat) => (editing?.components || []).filter((c) => catOfComp(c).key === cat.key).length;
  const catSum = (cat) => (editing?.components || []).filter((c) => catOfComp(c).key === cat.key).reduce((s, c) => s + perPcs(c), 0);
  const hasVariantSpecific = variantColors.length > 0;
  const kainTotalYard = (editing?.components || []).filter((c) => c.material_kind === "fabric").reduce((s, c) => s + (parseFloat(c.usage_per_pcs) || 0), 0);
  const kainTotalWaste = (editing?.components || []).filter((c) => c.material_kind === "fabric").reduce((s, c) => s + (parseFloat(c.usage_per_pcs) || 0) * (1 + (parseFloat(c.waste_pct) || 0) / 100), 0);

  const save = async () => {
    if (!editing.components.length) return toast.error("Resep masih kosong — tambah minimal satu bahan");
    const payload = { product_id: product.id, note: editing.note, components: editing.components.map((c) => ({ kind: normalizeKind(c), material_kind: c.material_kind, material_id: c.material_id || null, name: c.name, unit: c.unit, usage_per_pcs: parseFloat(c.usage_per_pcs) || 0, waste_pct: parseFloat(c.waste_pct) || 0, unit_price: parseFloat(c.unit_price) || 0, variant_color: c.variant_color || null })) };
    setSaving(true);
    try {
      await api.post("/production/boms", payload); // selalu buat versi baru & aktifkan
      toast.success(`Resep "${product.name}" tersimpan sebagai versi aktif`);
      const r = await api.get("/production/boms", { params: { product_id: product.id } });
      setBoms(r.data); setEditing(draftFrom(r.data.find((b) => b.active) || null)); idx.reload();
      if (window.parent !== window) window.parent.postMessage({ type: "bom-saved", productId: product.id }, "*");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const activate = async (b) => { try { await api.post(`/production/boms/${b.id}/activate`); toast.success(`Versi v${b.version} diaktifkan`); const r = await api.get("/production/boms", { params: { product_id: product.id } }); setBoms(r.data); setEditing(draftFrom(r.data.find((x) => x.active) || null)); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const del = async (b) => { if (!window.confirm(`Hapus resep v${b.version}?`)) return; try { await api.delete(`/production/boms/${b.id}`); toast.success("Versi dihapus"); reload(product.id); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const copyFrom = (srcComponents) => {
    setEditing((e) => ({ ...e, components: srcComponents.map((c) => ({ ...c, material_id: c.material_id || "" })) }));
    setCopyOpen(false); toast.success("Resep disalin — periksa & simpan untuk menerapkan");
  };

  const currentCat = CATS.find((c) => c.key === tab);

  const idxColumns = [
    { key: "name", label: "Produk", sortable: true, render: (p) => <span className="font-semibold text-cocoa">{p.name}</span> },
    { key: "code", label: "Kode", render: (p) => <span className="font-mono text-xs text-wmuted">{p.code || "-"}</span> },
    { key: "category", label: "Kategori", sortable: true, render: (p) => p.category || <span className="text-wmuted">-</span> },
    { key: "has_bom", label: "Status BOM", render: (p) => p.has_bom
        ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-status-passed/15 text-status-passed font-medium" data-testid={`bom-status-${p.id}`}>Sudah{p.bom_version ? ` v${p.bom_version}` : ""}{p.variant_bom_count ? ` +${p.variant_bom_count} warna` : ""}</span>
        : <span className="text-[11px] px-2 py-0.5 rounded-full bg-status-reject/15 text-status-reject font-medium" data-testid={`bom-status-${p.id}`}>Belum ada</span> },
    { key: "components_count", label: "Bahan", align: "right", render: (p) => p.has_bom ? p.components_count : "-" },
    { key: "hpp_estimate", label: "HPP/Pcs", align: "right", render: (p) => p.has_bom ? fmtIDR(p.hpp_estimate) : "-" },
    { key: "actions", label: "Aksi", align: "right", render: (p) => <Button size="sm" variant="outline" className="border-cocoa text-cocoa" onClick={() => openProduct(p.id)} data-testid={`bom-open-${p.id}`}>{p.has_bom ? "Lihat / Edit" : "Buat BOM"}</Button> },
  ];

  return (
    <div data-testid="bom-page">
      <PageHeader title="Master Resep Produksi" subtitle="Komposisi bahan per produk (kain, aksesori, label, packaging, ongkos) — disimpan & otomatis dipakai saat produksi berikutnya" />

      {!product && (
        <div className="bg-white border border-taupe/20 rounded-lg p-4 mb-4">
          <Label>Cari Produk</Label>
          <div className="relative max-w-xl mt-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-wmuted" />
            <Input value={idx.q} onChange={(e) => idx.setQ(e.target.value)} placeholder="Cari nama / kode produk..." className="pl-9 bg-white" data-testid="bom-product-search" />
          </div>
        </div>
      )}

      {product && editing && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => { setProduct(null); setEditing(null); }} data-testid="bom-back-to-list" className="text-cocoa">← Daftar Produk</Button>
              <div>
                <div className="font-heading font-bold text-cocoa text-lg">{product.name}</div>
                {activeBom
                  ? <div className="text-xs text-status-passed">Resep aktif v{activeBom.version} · HPP {fmtIDR(activeBom.hpp_estimate)}/pcs</div>
                  : <div className="text-xs text-status-rejected">Belum ada resep tersimpan — buat lalu klik "Simpan sebagai Resep".</div>}
              </div>
            </div>
            <div className="flex gap-2">
              {write && <Button variant="outline" onClick={() => setCopyOpen(true)} data-testid="bom-copy-btn" className="border-taupe text-cocoa"><Copy className="w-4 h-4 mr-1.5" />Salin dari Produk Lain</Button>}
              <Button variant="outline" onClick={() => setShowVersions((v) => !v)} data-testid="bom-versions-toggle" className="border-taupe text-cocoa">{showVersions ? <ChevronDown className="w-4 h-4 mr-1.5" /> : <ChevronRight className="w-4 h-4 mr-1.5" />}Riwayat Versi ({boms.length})</Button>
            </div>
          </div>

          {showVersions && (
            <Panel className="mb-3">
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Versi</TableHead><TableHead>Komponen</TableHead><TableHead className="text-right">HPP/Pcs</TableHead><TableHead>Dibuat</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
                <TableBody>
                  {boms.map((b) => (
                    <TableRow key={b.id} data-testid={`bom-row-${b.id}`}>
                      <TableCell className="font-semibold">v{b.version}</TableCell>
                      <TableCell>{b.components?.length || 0} bahan</TableCell>
                      <TableCell className="text-right font-semibold text-cocoa">{fmtIDR(b.hpp_estimate)}</TableCell>
                      <TableCell className="text-sm">{b.created_by || "-"}</TableCell>
                      <TableCell>{b.active ? <span className="text-status-passed font-semibold">Aktif</span> : <span className="text-wmuted">Nonaktif</span>}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button size="icon" variant="ghost" title="Muat ke editor" onClick={() => { setEditing(draftFrom(b)); setTab("kain"); }} data-testid={`bom-load-${b.id}`}><FileText className="w-4 h-4 text-cocoa" /></Button>
                        {write && !b.active && <Button size="icon" variant="ghost" title="Aktifkan" onClick={() => activate(b)} data-testid={`bom-activate-${b.id}`}><CheckCircle2 className="w-4 h-4 text-status-passed" /></Button>}
                        {write && <Button size="icon" variant="ghost" title="Hapus" onClick={() => del(b)} data-testid={`bom-del-${b.id}`}><Trash2 className="w-4 h-4 text-status-rejected" /></Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!boms.length && <TableRow><TableCell colSpan={6} className="text-center text-wmuted py-6">Belum ada versi resep.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </Panel>
          )}

          {/* Tab bar */}
          <div className="flex flex-wrap gap-1 p-1 bg-white border border-taupe/20 rounded-lg mb-3">
            {CATS.map((c) => { const Icon = c.icon; const n = c.key === "catatan" ? 0 : catCount(c); return (
              <button key={c.key} type="button" onClick={() => setTab(c.key)} data-testid={`bom-tab-${c.key}`} className={`flex items-center gap-1.5 text-sm px-3 py-2 rounded-md font-medium transition-colors ${tab === c.key ? "bg-cocoa text-ivory" : "text-cocoa hover:bg-ivory"}`}>
                <Icon className="w-4 h-4" />{c.label}{c.key !== "catatan" && n > 0 && <span className={`text-[10px] px-1.5 rounded-full ${tab === c.key ? "bg-ivory/25" : "bg-cocoa/10"}`}>{n}</span>}
              </button>
            ); })}
          </div>

          <Panel className="p-4">
            {tab === "catatan" ? (
              <div><Label>Catatan Produksi</Label><Textarea rows={5} value={editing.note} onChange={(e) => setEditing({ ...editing, note: e.target.value })} placeholder="Instruksi khusus, urutan pengerjaan, catatan QC, dll." data-testid="bom-note" className="bg-white mt-1" /></div>
            ) : (
              <div className="grid lg:grid-cols-[1fr_260px] gap-4">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow className="hover:bg-transparent">
                      <TableHead className="w-40">{currentCat.group === "service" ? "Jenis" : (currentCat.key === "kain" ? "Kain (Warna/Motif)" : "Bahan")}</TableHead>
                      {currentCat.kinds.length > 1 && <TableHead className="w-36">Kategori</TableHead>}
                      {currentCat.key === "kain" && <TableHead className="w-36">Berlaku untuk</TableHead>}
                      <TableHead className="text-right">Kebutuhan/pcs</TableHead><TableHead>Satuan</TableHead>
                      <TableHead className="text-right">Waste %</TableHead><TableHead className="text-right">Harga/{currentCat.unit}</TableHead>
                      <TableHead className="text-right">Subtotal</TableHead><TableHead></TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {editing.components.map((c, absIdx) => catOfComp(c).key === currentCat.key ? (
                        <TableRow key={absIdx} data-testid={`bom-comp-${absIdx}`}>
                          <TableCell>
                            {currentCat.group === "service" ? (
                              <Input value={c.name} onChange={(e) => setComp(absIdx, { name: e.target.value })} className="w-40 bg-white" placeholder="Nama ongkos" data-testid={`bom-comp-name-${absIdx}`} />
                            ) : (
                              <Select value={c.material_id || "none"} onValueChange={(v) => onMaterial(absIdx, currentCat.group, v === "none" ? "" : v)}>
                                <SelectTrigger className="w-52 bg-white" data-testid={`bom-comp-material-${absIdx}`}><SelectValue placeholder="Pilih bahan" /></SelectTrigger>
                                <SelectContent><SelectItem value="none">- Pilih -</SelectItem>{(currentCat.group === "fabric" ? fabrics : accessories).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                              </Select>
                            )}
                          </TableCell>
                          {currentCat.kinds.length > 1 && (
                            <TableCell>
                              <Select value={c.kind} onValueChange={(v) => setComp(absIdx, { kind: v, name: currentCat.group === "service" ? (currentCat.kinds.find(([kv]) => kv === v)?.[1] || c.name) : c.name })}>
                                <SelectTrigger className="w-36 bg-white" data-testid={`bom-comp-kind-${absIdx}`}><SelectValue /></SelectTrigger>
                                <SelectContent>{currentCat.kinds.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                              </Select>
                            </TableCell>
                          )}
                          {currentCat.key === "kain" && (
                            <TableCell>
                              <Select value={c.variant_color || "__all__"} onValueChange={(v) => setComp(absIdx, { variant_color: v === "__all__" ? "" : v })}>
                                <SelectTrigger className="w-36 bg-white" data-testid={`bom-comp-color-${absIdx}`}><SelectValue /></SelectTrigger>
                                <SelectContent><SelectItem value="__all__">Semua warna</SelectItem>{productColors.map((col) => <SelectItem key={col} value={col}>{col}</SelectItem>)}</SelectContent>
                              </Select>
                            </TableCell>
                          )}
                          <TableCell><Input type="number" value={c.usage_per_pcs} onChange={(e) => setComp(absIdx, { usage_per_pcs: e.target.value })} className="w-24 bg-white text-right" data-testid={`bom-comp-usage-${absIdx}`} /></TableCell>
                          <TableCell className="text-sm text-wmuted">{c.unit}</TableCell>
                          <TableCell><Input type="number" value={c.waste_pct} onChange={(e) => setComp(absIdx, { waste_pct: e.target.value })} className="w-20 bg-white text-right" data-testid={`bom-comp-waste-${absIdx}`} /></TableCell>
                          <TableCell><Input type="number" value={c.unit_price} onChange={(e) => setComp(absIdx, { unit_price: e.target.value })} className="w-28 bg-white text-right" data-testid={`bom-comp-price-${absIdx}`} /></TableCell>
                          <TableCell className="text-right font-semibold text-cocoa whitespace-nowrap">{fmtIDR(subtotal(c))}</TableCell>
                          <TableCell><Button size="icon" variant="ghost" onClick={() => rmComp(absIdx)} data-testid={`bom-comp-rm-${absIdx}`}><Trash2 className="w-4 h-4 text-status-rejected" /></Button></TableCell>
                        </TableRow>
                      ) : null)}
                      {catCount(currentCat) === 0 && <TableRow><TableCell colSpan={8} className="text-center text-wmuted py-6">Belum ada {currentCat.label.toLowerCase()}. Klik "Tambah".</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                  {write && <div className="mt-3 flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={() => addComp(currentCat)} data-testid={`bom-add-${currentCat.key}`} className="border-taupe text-cocoa"><Plus className="w-4 h-4 mr-1.5" />Tambah {currentCat.label}</Button>
                    {currentCat.key === "kain" && <Button variant="outline" size="sm" onClick={applyRecipeToAllColors} data-testid="bom-fill-colors-btn" className="border-cocoa text-cocoa" title="Salin resep dari warna contoh ke semua warna produk yang belum diisi (warna kain dipetakan otomatis)">Lengkapi Warna Lain</Button>}
                    {currentCat.key === "kain" && <Button variant="outline" size="sm" onClick={applyCuttingAvg} data-testid="bom-cutting-avg-btn" className="border-cocoa text-cocoa" title="Isi Kebutuhan/pcs dari rata-rata cutting yang sudah selesai">Ambil Rata-rata Cutting</Button>}
                  </div>}
                </div>

                {currentCat.key === "kain" ? (
                  <div className="rounded-lg bg-cocoa/5 border border-cocoa/15 p-3 h-fit">
                    <div className="text-xs font-semibold text-cocoa mb-2">Informasi Kain</div>
                    <div className="flex justify-between text-sm py-1"><span className="text-wmuted">Total Kain/pcs (tanpa waste)</span><b className="text-cocoa">{num(kainTotalYard)} yd</b></div>
                    <div className="flex justify-between text-sm py-1"><span className="text-wmuted">Total Kain/pcs (dengan waste)</span><b className="text-cocoa">{num(kainTotalWaste)} yd</b></div>
                    <div className="flex justify-between text-sm py-1 border-t border-cocoa/10 mt-1 pt-1"><span className="text-wmuted">Biaya Kain / pcs{hasVariantSpecific ? " (rata-rata varian)" : ""}</span><b className="text-cocoa" data-testid="bom-kain-cost">{fmtIDR(catSum(CATS[0]))}</b></div>
                    <div className="mt-2 text-[11px] text-wmuted bg-ivory rounded p-2">Perhitungan waste otomatis: kebutuhan/pcs + (kebutuhan × waste%).{hasVariantSpecific ? ` Kain per-warna dirata-rata ke ${nVar} varian karena 1 pcs hanya memakai 1 warna.` : ""}</div>
                  </div>
                ) : (
                  <div className="rounded-lg bg-cocoa/5 border border-cocoa/15 p-3 h-fit">
                    <div className="text-xs font-semibold text-cocoa mb-2">Subtotal {currentCat.label}</div>
                    <div className="text-xl font-heading font-bold text-cocoa">{fmtIDR(catSum(currentCat))}<span className="text-xs font-normal text-wmuted"> /pcs</span></div>
                    <div className="text-xs text-wmuted mt-1">{catCount(currentCat)} item</div>
                  </div>
                )}
              </div>
            )}
          </Panel>

          {/* Preview + Save */}
          <div className="rounded-lg bg-ivory/60 border border-taupe/20 p-3 mt-3">
            <div className="text-xs font-semibold text-cocoa mb-2">Preview Resep Produksi</div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {CATS.filter((c) => c.key !== "catatan").map((c) => (
                <button key={c.key} type="button" onClick={() => setTab(c.key)} className="bg-white rounded-lg border border-taupe/20 p-2 text-left hover:border-cocoa/40 transition-colors" data-testid={`bom-preview-${c.key}`}>
                  <div className="text-[11px] text-wmuted">{c.label}</div>
                  <div className="text-sm font-semibold text-cocoa">{catCount(c)} item</div>
                  <div className="text-[11px] text-cocoa/70">{fmtIDR(catSum(c))}/pcs</div>
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 mt-3 pt-3 border-t border-taupe/20">
            <div>
              <div className="text-xs text-wmuted">HPP Estimasi / Pcs</div>
              <div className="text-2xl font-heading font-bold text-cocoa" data-testid="bom-hpp-total">{fmtIDR(hpp)}</div>
              {hasVariantSpecific && <div className="text-[11px] text-wmuted mt-0.5">Kain/bahan per-warna dihitung rata-rata {nVar} varian (1 pcs = 1 warna), bukan dijumlah semua.</div>}
            </div>
            <div className="flex gap-2">
              {activeBom && <Button variant="outline" onClick={() => setEditing(draftFrom(activeBom))} className="border-taupe text-cocoa">Reset ke Aktif</Button>}
              {write && <Button onClick={save} disabled={saving} data-testid="bom-save-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory"><Save className="w-4 h-4 mr-1.5" />{saving ? "Menyimpan..." : `Simpan sebagai Resep ${product.name.length > 18 ? product.name.slice(0, 18) + "…" : product.name}`}</Button>}
            </div>
          </div>

          <CopyRecipeDialog open={copyOpen} setOpen={setCopyOpen} currentId={product.id} onCopy={copyFrom} />
        </>
      )}

      {!product && (
        <>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Select value={hasBom} onValueChange={setHasBom}>
              <SelectTrigger className="w-52 bg-white" data-testid="bom-hasbom-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Produk</SelectItem>
                <SelectItem value="yes">Sudah ada BOM</SelectItem>
                <SelectItem value="no">Belum ada BOM</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <ActiveFilters chips={hasBom !== "all" ? [{ key: "hasbom", label: hasBom === "yes" ? "Sudah ada BOM" : "Belum ada BOM", onClear: () => setHasBom("all") }] : []} onReset={hasBom !== "all" ? () => setHasBom("all") : undefined} />
          <DataTable
            testid="bom-product-index"
            columns={idxColumns}
            rows={idx.items}
            loading={idx.loading}
            error={idx.error}
            sort={idx.sort}
            onSort={idx.toggleSort}
            page={idx.page} pages={idx.pages} total={idx.total} limit={idx.limit}
            onPageChange={idx.setPage} onLimitChange={idx.setLimit}
            searching={!!idx.q}
            emptyLabel="Tidak ada produk."
          />
        </>
      )}
    </div>
  );
}

function CopyRecipeDialog({ open, setOpen, currentId, onCopy }) {
  const [pq, setPq] = useState("");
  const [products, setProducts] = useState([]);
  useEffect(() => { if (!open) { setPq(""); setProducts([]); } }, [open]);
  useEffect(() => { const t = setTimeout(() => { if (pq.length >= 2) api.get("/products", { params: { q: pq } }).then((r) => setProducts(r.data.filter((p) => p.id !== currentId).slice(0, 15))); else setProducts([]); }, 350); return () => clearTimeout(t); }, [pq, currentId]);
  const pick = async (p) => {
    try {
      const r = await api.get("/production/boms", { params: { product_id: p.id } });
      const src = r.data.find((b) => b.active) || r.data[0];
      if (!src || !(src.components || []).length) return toast.error(`"${p.name}" belum punya resep`);
      onCopy(src.components);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">Salin Resep dari Produk Lain</DialogTitle><DialogDescription className="text-xs text-wmuted">Komponen resep aktif produk sumber akan dimuat ke editor. Simpan untuk menerapkan.</DialogDescription></DialogHeader>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-wmuted" />
          <Input value={pq} onChange={(e) => setPq(e.target.value)} placeholder="Cari produk sumber..." className="pl-9" data-testid="bom-copy-search" autoFocus />
        </div>
        <div className="max-h-72 overflow-y-auto">
          {products.map((p) => <button key={p.id} onClick={() => pick(p)} data-testid={`bom-copy-src-${p.id}`} className="block w-full text-left px-3 py-2 hover:bg-ivory rounded text-sm"><span className="font-medium text-cocoa">{p.name}</span> <span className="text-wmuted text-xs">{p.category}</span></button>)}
          {pq.length >= 2 && !products.length && <div className="text-center text-wmuted text-sm py-4">Tidak ada produk lain.</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
